import pickBy from 'lodash/pickBy';
import {
  IGuest_EventNew,
  IGuest_Invitation,
  Rsvp,
} from '@bridebook/models/source/models/Weddings/Guests.types';
import { OverviewRsvpStatusesUI, RsvpStatusesUI } from '@bridebook/web/lib/guestlist/constants';
import type { IWeddingEvent } from '@bridebook/models/source/models/Weddings/Events.types';

export const onlyInvitedEventsById: (
  eventsById: IGuest_Invitation['events'],
) => IGuest_Invitation['events'] = (eventsById: IGuest_Invitation['events']) =>
  pickBy(eventsById, (event) => !!event);

export const filterInvitationByExistingEvents = (
  events: IWeddingEvent[],
  invitationEvents: IGuest_Invitation['events'] | undefined,
): IGuest_Invitation['events'] =>
  pickBy(invitationEvents || {}, (_, key) => events.some((event) => event.id === key));

/**
 * Returns the RSVP status for the overview page
 * @param events
 * @param invitation - The RSVP object
 * @returns The RSVP status for the overview page or undefined if no RSVP is found (for example if the guest is not invited to any events)
 */
export const getOverviewRsvpStatus = (
  events: IWeddingEvent[],
  invitation: IGuest_Invitation | undefined,
): OverviewRsvpStatusesUI | undefined => {
  if (!invitation) {
    return undefined;
  }
  const invitedEvents = filterInvitationByExistingEvents(
    events,
    onlyInvitedEventsById(invitation.events),
  );
  if (Object.keys(invitedEvents).length === 0) {
    return undefined;
  }
  return Object.values(invitedEvents).every((event) => event.rsvp !== 'pending')
    ? OverviewRsvpStatusesUI.responded
    : OverviewRsvpStatusesUI.pending;
};

/**
 * Returns the RSVP response for a given event
 * @param invitation - The RSVP object
 * @param eventId - The ID of the event
 * @returns The RSVP response for the given event
 */
export const getRsvpResponse = (
  invitation: IGuest_Invitation | undefined,
  eventId: string,
): NonNullable<IGuest_EventNew['rsvp']> => {
  if (!invitation || !invitation?.events[eventId] || !invitation?.events?.[eventId]?.rsvp) {
    return Rsvp.PENDING;
  }
  const response = invitation.events[eventId].rsvp;
  if (response === undefined) return Rsvp.PENDING;
  return response;
};

/**
 * Returns the RSVP response for the overview page
 * @param events
 * @param invitation - The RSVP object
 * @returns The RSVP response for the overview page
 */
export const getOverviewRsvpResponse = (
  events: IWeddingEvent[],
  invitation: IGuest_Invitation | undefined,
): IGuest_EventNew['rsvp'] | 'mixedResponse' | undefined => {
  const filteredEvents = filterInvitationByExistingEvents(events, invitation?.events);
  const rsvps = Object.values(filteredEvents);

  if (rsvps.length === 0) {
    return Rsvp.PENDING;
  }

  if (rsvps.every((info) => info.rsvp === Rsvp.YES)) {
    return Rsvp.YES;
  }

  if (rsvps.every((info) => info.rsvp === Rsvp.NO)) {
    return Rsvp.NO;
  }

  if (rsvps.every((info) => !info.rsvp || info.rsvp === Rsvp.PENDING)) {
    return Rsvp.PENDING;
  }

  return 'mixedResponse';
};

/**
 * Converts an RSVP response to RsvpStatusesUI type
 * @param rsvp - The RSVP response
 * @returns The UI RSVP status
 */
export const toUIRsvpStatus = (rsvp: IGuest_EventNew['rsvp']): RsvpStatusesUI => {
  switch (rsvp) {
    case 'yes':
      return RsvpStatusesUI.yes;
    case 'no':
      return RsvpStatusesUI.no;
    case 'pending':
      return RsvpStatusesUI.noReply;
    default:
      return RsvpStatusesUI.noReply;
  }
};
